/**
 * IAM user hooks — list/create/detail/delete plus policy attach/detach. Every
 * hook is scoped by `serviceId` (the connection + credential is resolved
 * server-side from it), so all query keys carry it to keep caches isolated.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { serviceApiV3, unwrap, s3Keys } from './_shared';
import { iamBase, iamPath, type AttachedPolicy } from './iam-shared';

/** One IAM user as listed (`GET /iam/users`). */
export interface IamUser {
  user_name: string;
  user_id?: string;
  arn?: string;
  create_date?: string | null;
}

/** A group a user belongs to (shown read-only on the user detail). */
export interface UserGroupRef {
  group_name: string;
  arn?: string;
}

/** An access key owned by a user (read-only display; secret never returned). */
export interface UserAccessKey {
  access_key_id: string;
  status?: string;
  create_date?: string | null;
}

/** Full user with its attachments, memberships and keys (`GET /iam/users/:name`). */
export interface IamUserDetail {
  user: IamUser;
  attached_policies: AttachedPolicy[];
  groups: UserGroupRef[];
  access_keys: UserAccessKey[];
}

/** List the service's IAM users. */
export function useIamUsers(serviceId: string | undefined) {
  return useQuery<IamUser[]>({
    queryKey: s3Keys.iamUsers(serviceId ?? ''),
    enabled: !!serviceId,
    staleTime: 30_000,
    queryFn: async () => {
      const res = await serviceApiV3.get(`${iamBase(serviceId!)}/users`);
      return unwrap<{ users?: IamUser[] }>(res)?.users ?? [];
    },
  });
}

/** Fetch one user with attached policies, group memberships and access keys. */
export function useIamUser(serviceId: string | undefined, name: string | undefined) {
  return useQuery<IamUserDetail>({
    queryKey: s3Keys.iamUser(serviceId ?? '', name ?? ''),
    enabled: !!serviceId && !!name,
    queryFn: async () =>
      unwrap<IamUserDetail>(await serviceApiV3.get(iamPath(serviceId!, 'users', name!))),
  });
}

/** Create an IAM user by name. */
export function useCreateIamUser(serviceId: string) {
  const qc = useQueryClient();
  return useMutation<IamUser, Error, string>({
    mutationFn: async (userName) =>
      unwrap<IamUser>(
        await serviceApiV3.post(`${iamBase(serviceId)}/users`, { user_name: userName }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: s3Keys.iamUsers(serviceId) }),
  });
}

/** Delete a user — its access keys and attachments go with it. */
export function useDeleteIamUser(serviceId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (name) => {
      await serviceApiV3.delete(iamPath(serviceId, 'users', name));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: s3Keys.iamUsers(serviceId) }),
  });
}

/** Attach or detach a managed policy on a user (shared shape, `attach` toggles verb). */
export function useUserPolicyMutation(serviceId: string, attach: boolean) {
  const qc = useQueryClient();
  return useMutation<void, Error, { name: string; policyArn: string }>({
    mutationFn: async ({ name, policyArn }) => {
      const verb = attach ? 'attach-policy' : 'detach-policy';
      await serviceApiV3.post(iamPath(serviceId, 'users', name, `/${verb}`), {
        policy_arn: policyArn,
      });
    },
    onSuccess: (_r, { name }) =>
      qc.invalidateQueries({ queryKey: s3Keys.iamUser(serviceId, name) }),
  });
}
